/**
 * useNotificationStore — 通知状态管理（Zustand）
 * - 管理通知列表、未读数、加载状态
 * - fetchNotifications：从后端拉取通知列表（分页），并单独刷新未读数
 * - markAsRead：乐观更新已读状态（先更新 UI，后台调 API）
 * - addNotification：SSE 实时推送时追加新通知到列表头部
 */
import {create} from 'zustand'
import type {Notification} from '@/types'
import {notificationsApi} from '@/api/notifications'

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  fetchNotifications: (page?: number, options?: {forceUnread?: boolean}) => Promise<void>
  refreshUnreadCount: (options?: {force?: boolean}) => Promise<number>
  markAsRead: (id: number) => void
  addNotification: (notification: Notification) => void
  setUnreadCount: (count: number) => void
}

const unreadCountCacheMs = 3000
let unreadCountRequest: Promise<number> | null = null
let lastUnreadCountFetchedAt = 0
let unreadCountRequestId = 0

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,

  /**
   * 拉取通知列表，同时刷新未读数量。
   *
   * 后端通知列表是标准分页结构：items/total/page/pageSize。
   * 未读数是另一个轻量接口，分开请求能避免 unreadCount 被 undefined 覆盖。
   */
  fetchNotifications: async (page = 1, options = {}) => {
    set({loading: true})
    try {
      const [res] = await Promise.all([
        notificationsApi.getList({page, pageSize: 20}),
        get().refreshUnreadCount({force: options.forceUnread})
      ])
      set({
        notifications: res.items,
        loading: false
      })
    } catch {
      set({loading: false})
    }
  },

  /**
   * 刷新未读数，并对短时间内的重复调用做去重。
   *
   * 打开新页面时 React StrictMode、Layout 初始化、SSE 预检和通知页列表可能几乎同时触发。
   * 这里合并并发请求，并复用 3 秒内的结果，避免 /notifications/unread-count 被重复打爆。
   */
  refreshUnreadCount: async (options = {}) => {
    const now = Date.now()
    if (!options.force && lastUnreadCountFetchedAt && now - lastUnreadCountFetchedAt < unreadCountCacheMs) {
      return get().unreadCount
    }

    if (!options.force && unreadCountRequest) {
      return unreadCountRequest
    }

    const requestId = unreadCountRequestId + 1
    unreadCountRequestId = requestId

    const request = notificationsApi
      .getUnreadCount()
      .then((result) => {
        if (requestId === unreadCountRequestId) {
          lastUnreadCountFetchedAt = Date.now()
          set({unreadCount: result.count})
        }
        return result.count
      })
      .finally(() => {
        if (unreadCountRequest === request) {
          unreadCountRequest = null
        }
      })

    unreadCountRequest = request
    return unreadCountRequest
  },

  /** 标记单条通知为已读（乐观更新） */
  markAsRead: (id) => {
    notificationsApi.markAsRead(id)
    const {notifications, unreadCount} = get()
    set({
      notifications: notifications.map((n) => (n.id === id ? {...n, readAt: new Date().toISOString()} : n)),
      unreadCount: Math.max(0, unreadCount - 1)
    })
  },

  /**
   * SSE 推送新通知时调用，插入列表头部并增加未读数。
   *
   * EventSource 断线重连或多个布局重复挂载时，理论上可能收到同一条通知。
   * 这里按通知 id 去重，避免通知中心出现重复行、未读数被重复 +1。
   */
  addNotification: (notification) => {
    const {notifications, unreadCount} = get()
    const existing = notifications.find((item) => item.id === notification.id)

    if (existing) {
      set({
        notifications: [notification, ...notifications.filter((item) => item.id !== notification.id)],
        unreadCount: existing.readAt && !notification.readAt ? unreadCount + 1 : unreadCount
      })
      return
    }

    set({
      notifications: [notification, ...notifications],
      unreadCount: notification.readAt ? unreadCount : unreadCount + 1
    })
  },

  setUnreadCount: (count) => {
    unreadCountRequestId += 1
    lastUnreadCountFetchedAt = 0
    set({unreadCount: count})
  }
}))
